import React, { useState } from "react";
import { CornerOrn, EyeMark } from "@/components/Ornaments";
import { PageHead } from "@/components/PageHead";
import { Seo } from "@/components/Seo";
import type { LanguageBinding } from "@/hooks/useLanguage";
import { cx } from "@/lib/classnames";

interface TimelineItem {
  y: string;
  t: string;
  b?: string;
  items?: readonly { readonly y: string; readonly t: string }[];
}

const TimelineRow: React.FC<{
  tl: TimelineItem;
  isRTL: boolean;
}> = ({ tl, isRTL }) => {
  const [open, setOpen] = useState(false);
  const hasDetail = Boolean(tl.b || (tl.items && tl.items.length > 0));
  return (
    <li
      className="pb-5"
      style={{ borderBottom: "1px solid var(--hairline-light)" }}
    >
      <button
        type="button"
        onClick={() => hasDetail && setOpen((v) => !v)}
        disabled={!hasDetail}
        className={cx(
          "group w-full grid gap-4 md:gap-6 items-baseline text-start",
          "grid-cols-[110px_1fr_auto] md:grid-cols-[150px_1fr_auto]",
          hasDetail && "cursor-pointer",
        )}
        aria-expanded={open}
      >
        <div
          className="ff-en-serif text-xl md:text-2xl tabular-nums"
          style={{ color: "var(--vermillion)", fontWeight: 500 }}
        >
          {tl.y}
        </div>
        <div
          className={cx(isRTL ? "ff-ar-serif" : "ff-en-serif", "text-lg md:text-xl")}
          style={{ color: "var(--ink)", fontWeight: 600 }}
        >
          {tl.t}
        </div>
        {hasDetail ? (
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            className="transition-transform shrink-0"
            style={{
              color: "var(--muted-light)",
              transform: open ? "rotate(180deg)" : "rotate(0)",
            }}
            aria-hidden
          >
            <path d="M3 5 L7 9 L11 5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          <span />
        )}
      </button>

      {open && hasDetail && (
        <div
          className={cx(
            "mt-3 grid gap-4 md:gap-6",
            "grid-cols-[110px_1fr] md:grid-cols-[150px_1fr]",
          )}
        >
          <div />
          <div className="space-y-2">
            {tl.b && (
              <p
                className={cx(
                  isRTL ? "ff-ar-sans" : "ff-en-body",
                  "leading-relaxed",
                )}
                style={{ color: "var(--muted-light)" }}
              >
                {tl.b}
              </p>
            )}
            {tl.items && tl.items.length > 0 && (
              <ul className="mt-2 space-y-1.5">
                {tl.items.map((it, i) => (
                  <li
                    key={i}
                    className="grid grid-cols-[84px_1fr] gap-3 items-baseline"
                  >
                    <span
                      className={cx(
                        "cat-num text-[12px] tabular-nums",
                        "opacity-80",
                      )}
                      style={{ color: "var(--vermillion)" }}
                    >
                      {it.y}
                    </span>
                    <span
                      className={cx(
                        isRTL ? "ff-ar-sans" : "ff-en-body",
                        "text-sm md:text-base leading-snug",
                      )}
                      style={{ color: "var(--ink-2)" }}
                    >
                      {it.t}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </li>
  );
};

export const About: React.FC<{ language: LanguageBinding }> = ({ language }) => {
  const { t, lang, isRTL } = language;
  return (
    <>
      <Seo
        title={t.meta.about.title}
        description={t.meta.about.description}
        lang={lang}
        path="/about"
      />
      <PageHead
        kicker={t.aboutPage.kicker}
        title={t.aboutPage.title}
        tagline={t.aboutPage.role}
        isRTL={isRTL}
      />

      <section className="relative parchment-deckle">
        <div className="grain-soft" />
        <div className="max-w-page mx-auto px-6 md:px-10 py-20 md:py-24 relative">
          <div className="grid md:grid-cols-12 gap-12">
            <div className="md:col-span-5">
              <figure
                className="relative aspect-[3/4] w-full overflow-hidden"
                style={{ background: "#f2ebdb" }}
              >
                <img
                  src={`${import.meta.env.BASE_URL}author/portrait.jpg`}
                  alt={t.aboutPage.title}
                  className="absolute inset-0 h-full w-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute top-3 left-3 opacity-70" style={{ color: "var(--gold)" }}>
                  <CornerOrn />
                </div>
                <div className="absolute top-3 right-3 opacity-70" style={{ color: "var(--gold)" }}>
                  <CornerOrn flip />
                </div>
                <div
                  className="absolute bottom-3 left-3 opacity-70 rotate-180"
                  style={{ color: "var(--gold)" }}
                >
                  <CornerOrn flip />
                </div>
                <div
                  className="absolute bottom-3 right-3 opacity-70 rotate-180"
                  style={{ color: "var(--gold)" }}
                >
                  <CornerOrn />
                </div>
              </figure>
            </div>

            <div className="md:col-span-7">
              <p
                className={cx(
                  isRTL ? "ff-ar-serif" : "ff-en-serif italic",
                  "text-2xl md:text-3xl leading-snug",
                )}
                style={{ color: "var(--ink)" }}
              >
                {t.aboutPage.bioLead}
              </p>

              {/* Authoritative biographical paragraphs taken from the
                  Arabic edition's back cover. Visually distinct from the
                  surrounding editorial copy: a thin gold-rule, a small
                  source label, and a slightly tighter typographic scale
                  so it reads as a quoted authorial source rather than
                  as site-written commentary. */}
              <div className="mt-10">
                <div
                  className="h-px mb-4"
                  style={{ background: "var(--gold)" }}
                />
                <div
                  className={cx(
                    isRTL ? "ff-ar-sans" : "ff-en-sc tracked-widest",
                    "text-[11px] mb-4",
                  )}
                  style={{ color: "var(--vermillion)" }}
                >
                  {t.aboutPage.bioFromBackCoverHeading}
                </div>
                <div
                  className={cx(
                    isRTL ? "ff-ar-serif" : "ff-en-serif",
                    "text-base md:text-lg leading-relaxed space-y-4",
                  )}
                  style={{ color: "var(--ink)" }}
                >
                  {t.aboutPage.bioFromBackCover.map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>
                <div
                  className="h-px mt-6"
                  style={{ background: "var(--hairline-light)" }}
                />
              </div>

              <div
                className={cx(
                  isRTL ? "ff-ar-sans" : "ff-en-body",
                  "mt-8 text-lg leading-relaxed space-y-5",
                )}
                style={{ color: "var(--ink-2)" }}
              >
                {t.aboutPage.bioBody.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>

              <div className="mt-14">
                <div
                  className={cx(
                    isRTL ? "ff-ar-sans" : "ff-en-sc tracked-widest",
                    "text-[11px] mb-6",
                  )}
                  style={{ color: "var(--vermillion)" }}
                >
                  {t.aboutPage.timelineTitle}
                </div>
                <ul className="space-y-3">
                  {t.aboutPage.timeline.map((tl, i) => (
                    <TimelineRow key={i} tl={tl} isRTL={isRTL} />
                  ))}
                </ul>
              </div>

              <div
                className="mt-14 p-8"
                style={{
                  background: "rgba(20,17,14,.05)",
                  border: "1px solid var(--hairline-light)",
                }}
              >
                <div className="flex justify-start mb-3" style={{ color: "var(--gold)" }}>
                  <EyeMark size={28} />
                </div>
                <p
                  className={cx(
                    isRTL ? "ff-ar-serif" : "ff-en-serif italic",
                    "text-lg leading-relaxed",
                  )}
                  style={{ color: "var(--ink-2)" }}
                >
                  {t.aboutPage.memNote}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
